import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Badge,
  Avatar,
  Slide,
  Container,
  CircularProgress,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PolicyDetailsActionsMenu from "./PolicyDetailsActionsMenu";
// import docFilePath from "../../../../Assets/Docs/access-control-policy-bsi.docx";
import Logo from "../../../../Assets/Images/Logo.png";
import GroupMembersDialog from "../PolicyDialogs/GroupMembersDialog";
import UploadPolicyDialog from "../PolicyDialogs/UploadPolicyDialog";
import DownLoadTemplateDialog from "../PolicyDialogs/DownLoadTemplateDialog";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  GET_POLICY_LIST,
  GET_POLICY_APPROVAL_DETAILS,
  UPDATE_POLICY_STATUS,
  DELETE_TENANT_DOCUMENT,
} from "../../../../apis/settingsApis/policy-apis/policyApis";
import { useLocation, useNavigate } from "react-router-dom";
import { GET_STAFF_DETAILS } from "../../../../apis/organization-apis/people-apis/peopleApis";
import {
  GET_ROLES,
  GET_USER_DETAILS,
} from "../../../../apis/dashboard-apis/dashboardDetailApi";
import { LoadingButton } from "@mui/lab";
import RejectPolicyDialog from "../PolicyDialogs/RejectPolicyDialog";
import ActiveStaffCard from "./ActiveStaffCard";
import FrameworkListCard from "./FrameworkListCard";
import ControlsListCard from "./ControlsListCard";
import DetailCard from "./DetailCard";
import ChecksStatusCard from "./ChecksStatusCard";
import DocxViewer from "../../../../components/DocxViewer/DocxViewer";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";
import InformationStepsCard from "../../../../components/InformationStepsCard";
import PolicyApprovalSuggestionCard from "./PolicyApprovalSuggestionCard";
import GenerateDocButton from "../../../../components/GenerateDocButton/GenerateDocButton";
import useIsDemoInstance from "../../../Chat/Chat-Hooks/useIsDemoInstance";

const PolicyDetails = () => {
  const sectionRef = useRef(null);
  const isDemoInstance = useIsDemoInstance();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const policyId = queryParams.get("id");
  const {
    data: userData,
    loading: userDetailsLoading,
    error,
  } = useQuery(GET_USER_DETAILS);
  const { loading, data, refetch } = useQuery(GET_POLICY_LIST);
  const { data: roleDataResponse } = useQuery(GET_ROLES);
  const { data: staffList } = useQuery(GET_STAFF_DETAILS);
  const [updatePolicyStatusByUuid] = useMutation(UPDATE_POLICY_STATUS);
  const [deleteTenantDocument, { loading: deletingPolicy }] =
    useMutation(DELETE_TENANT_DOCUMENT);
  const [fetchApprovalDetails, { loading: approvalDetailsLoading, data: approvalDetailsData, refetch: refetchApprovalDetails }] =
    useLazyQuery(GET_POLICY_APPROVAL_DETAILS, { fetchPolicy: "network-only" });
  const [isOpen, setIsOpen] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeStaffs, setActiveStaffs] = useState([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const userId = userData?.getAccountDetails?.userDetails?.user_id || {};
  const activePolicies = data?.getAllTypePolicies.activePolicies || [];
  const pendingPolicies = data?.getAllTypePolicies.pendingPolicies || [];
  const draftPolicies = data?.getAllTypePolicies.draftPolicies || [];

  // const allPolicies = [...draftPolicies, ...pendingPolicies, ...activePolicies];
  const uniquePolicies = new Map();
  [...draftPolicies, ...pendingPolicies, ...activePolicies].forEach((p) => {
    uniquePolicies.set(p.policyId, { ...p });
  });
  const allPolicies = Array.from(uniquePolicies.values());

  // Find the policy by policy_id
  const policy = allPolicies.find(
    (p) => Number(p.policyId) === Number(policyId)
  );

  useEffect(() => {
    if (!loading && !policy) {
      setSnackbarInfo({
        message: "Policy not found. It may have been deleted.",
        vertical: "top",
        horizontal: "right",
      });
      navigate("/admin/policies");
    }
  }, [loading, policy, navigate]);

  const policyOwner = staffList?.getEmployees?.data?.find(
    (e) => e.employee_id === policy?.created_by_employee_id
  );

  // were using policy uploader and policy creator as different concepts,
  // for mvp, sticking to policy creator is the policy uploader
  // const policyOwner = staffList?.getEmployees?.data?.find(
  //   (e) => e.employee_id === policy?.owner_employee_id
  // );

  const policyApprover = staffList?.getEmployees?.data?.find(
    (e) => e.employee_id === policy?.approver_employee_id
  );

  const roleData = roleDataResponse?.getRoles;
  const approverRoleId = policy?.approver?.role_id;
  const ownerRoleId = policy?.owner?.role_id;

  const isDocxPolicy =
    policy?.filePath?.toLowerCase().endsWith(".docx") ||
    policy?.fileName?.toLowerCase().endsWith(".docx");

  const [cacheKey] = useState(Date.now());

  // Poll every 15s for is_file_valid when policy is analyzing (draft/pending and is_file_valid null)
  const isAnalyzing =
    policy &&
    (policy?.fileStatus === "draft" || policy?.fileStatus === "pending approval") &&
    policy?.is_file_valid == null;
  useEffect(() => {
    if (!isAnalyzing || !refetch) return;
    const intervalId = setInterval(() => {
      refetch(); // Refetch policy list
      refetchApprovalDetails?.(); // Refetch approval details
      setRefetchTrigger((prev) => prev + 1); // Trigger refetch for checks status
    }, 15_000);
    return () => clearInterval(intervalId);
  }, [isAnalyzing, refetch, refetchApprovalDetails]);

  // Fetch backend-extracted approval details when policy is draft or pending approval
  useEffect(() => {
    const fileId = policy?.fileId;
    const status = policy?.fileStatus;
    if (fileId && (status === "draft" || status === "pending approval" || status === "reject")) {
      fetchApprovalDetails({ variables: { file_id: Number(policy?.fileId) } });
    }
  }, [policy?.fileId, policy?.fileStatus, fetchApprovalDetails, refetchTrigger]);

  const approvalDetails = approvalDetailsData?.getPolicyApprovalDetails;

  useEffect(() => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  useEffect(() => {
    if (staffList && staffList.getEmployees) {
      const employees = staffList.getEmployees.data || [];
      const activeStaff = employees
        .filter((item) => item.in_scope !== false && !item.termination_date)
        .map((item) => {
          return {
            avatar: item.avatar,
            name: item.name,
            email: item?.emails[0]?.email,
          };
        });
      setActiveStaffs(activeStaff);
    }
  }, [staffList]);

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  // const handleDelete = () => {
  //   setSnackbarInfo({
  //     message: "Draft deleted successfully!",
  //     vertical: "top",
  //     horizontal: "right",
  //     Transition: TransitionComponent,
  //   });
  //   handleMenuClose();
  // };

  const handleUpdateStatus = async (file_uuid, status, reason, approval_date, approver_employee_id) => {
    try {
      const response = await updatePolicyStatusByUuid({
        variables: {
          file_uuid: String(file_uuid),
          status,
          reason: reason ?? null,
          approval_date: approval_date ?? null,
          approver_employee_id: approver_employee_id ?? null,
        },
      });
      setSnackbarInfo({
        message: response?.data?.updatePolicyStatusByUuid?.message,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } catch (error) {
      console.error("Error:", error);
      setSnackbarInfo({
        message: error?.message,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
  };

  const handleSendForApproval = async () => {
    setIsLoading(true);
    try {
      await handleUpdateStatus(policy?.fileUuid, "pending approval", null);
      refetch();
    } catch (error) {
      console.error("Error sending for approval:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (reason) => {
    setIsRejecting(true);
    try {
      await handleUpdateStatus(policy?.fileUuid, "reject", reason);
      refetch();
      setOpen(false);
    } catch (error) {
      console.error("Error rejecting:", error);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await handleUpdateStatus(policy?.fileUuid, "active", null);
      refetch();
    } catch (error) {
      console.error("Error approving:", error);
    }
    setIsApproving(false);
  };

  if (loading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }
  return (
    <>
      <Box pt={1}>
        <Box
          sx={{
            paddingY: 2,
            marginBottom: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            scrollMarginTop: "6rem",
          }}
          ref={sectionRef}
        >
          <Box display="flex" alignItems="center" gap="15px" sx={{ minWidth: 0, flex: 1 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Avatar
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: "primary.dark",
                    padding: "2px",
                  }}
                  src={Logo}
                />
              }
            >
              <Avatar
                sx={{
                  bgcolor: "#F7F7FC",
                  width: 48,
                  height: 48,
                  border: "1px solid #E0E0E0",
                }}
              >
                <DescriptionOutlinedIcon sx={{ color: "primary.main" }} />
              </Avatar>
            </Badge>

            <Box>
              <Box display="flex" alignItems="baseline" gap="8px">
                <Typography variant="h4">
                  {policy?.policyDisplayName}
                </Typography>
                <Chip
                  label={
                    policy?.fileStatus === "draft" ||
                      policy?.fileStatus === "pending approval"
                      ? `Draft:v${policy?.fileVersion}`
                      : policy?.fileStatus === "reject"
                        ? `Rejected Draft:v${policy?.fileVersion}`
                        : `Active:v${policy?.fileVersion}`
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    bgcolor:
                      policy?.fileStatus === "draft" ||
                        policy?.fileStatus === "pending approval"
                        ? "#f0f4ff"
                        : policy?.fileStatus === "reject"
                          ? "#FEEBEB"
                          : "#E8FBF3",
                    color:
                      policy?.fileStatus === "draft" ||
                        policy?.fileStatus === "pending approval"
                        ? "inherit"
                        : policy?.fileStatus === "reject"
                          ? "#C71414"
                          : "#057F28",
                    borderColor:
                      policy?.fileStatus === "draft" ||
                        policy?.fileStatus === "pending approval"
                        ? ""
                        : policy?.fileStatus === "reject"
                          ? "#C71414"
                          : "#057F28",
                  }}
                />
              </Box>
              {policyOwner?.employee_id === userId && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginTop: "5px" }}
                >
                  {`Needs approval from ${policy?.approver?.role_name}${policyApprover?.emails?.[0]?.email
                    ? ` (${policyApprover.emails[0].email})`
                    : ""
                    } who is not the uploader of policy`}
                  {/* • Last edited 10 hours ago */}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap="15px" sx={{ flexShrink: 0 }}>
            {isDemoInstance && (
              <GenerateDocButton
                label="Edit with Rego AI"
                file={{
                  fileId: policy?.policyId ?? policyId,
                  title: policy?.policyDisplayName,
                  docType: "policy",
                  status: policy?.fileStatus,
                  version: policy?.fileVersion,
                  sourceUrl: policy?.filePath,
                  returnTo: location.pathname + location.search,
                }}
                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
              />
            )}

            {(policy?.fileStatus === "draft" ||
              policy?.fileStatus === "reject") &&
              policy?.is_file_valid != null &&
              roleData?.find(
                (val) => Number(val?.role_id) === Number(ownerRoleId)
              ) &&
              policyOwner?.employee_id === userId && (
                <LoadingButton
                  variant="contained"
                  color="primary"
                  sx={{ marginLeft: "16px" }}
                  loading={isLoading || loading}
                  onClick={handleSendForApproval}
                >
                  Send for Approval
                </LoadingButton>
              )}

            {policy?.fileStatus === "pending approval" &&
              policy?.is_file_valid != null &&
              roleData?.find(
                (val) => Number(val?.role_id) === Number(approverRoleId)
              ) &&
              policyOwner?.employee_id !== userId &&
              !loading && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{ marginLeft: "16px" }}
                    onClick={() => setOpen(true)}
                  >
                    Reject
                  </Button>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    // sx={{ marginLeft: "10px" }}
                    loading={isApproving}
                    onClick={handleApprove}
                  >
                    Approve
                  </LoadingButton>
                </>
              )}

            <PolicyDetailsActionsMenu
              policy={policy}
              onEditDocument={() => setOpenEditDialog(true)}
              onDownloadTemplate={() => setOpenTemplateDialog(true)}
              onDownloadError={(message) =>
                setSnackbarInfo({ message, vertical: "top", horizontal: "right" })
              }
              onRemovePolicy={() => setOpenRemoveDialog(true)}
            />
          </Box>
        </Box>

        {/* Information Steps Card - Draft Status */}
        {policy?.fileStatus === "draft" && (
          <InformationStepsCard
            heading="**What should I do with this draft policy?**"
            subtitle={`This is a draft version of **${policy?.policyDisplayName
              }**. This policy follows a **maker-checker process** where **${policyOwner?.name || "the uploader"
              }** (who uploaded this policy) creates it, and any admin (except the uploader) can review and approve it.`}
            steps={[
              "**Review the policy document** to ensure all content is accurate and complete.",
              "**Edit the document** if needed using the menu options (⋯) above.",
              `**Submit for approval** once ready. Only **${policyOwner?.name || "the uploader"
              }** can submit this policy for approval. The policy will then be available for any admin (except the uploader) to review and approve.`,
              "Any admin who is **not the uploader** can approve or reject the submission. The uploader cannot approve their own policy, ensuring proper separation of duties in the maker-checker process.",
              "Once approved by an admin, the policy becomes **active** and employees can acknowledge it.",
            ]}
            footerText={`💡 As the **uploader** (${policyOwner?.name || "who uploaded this policy"
              }), you can create, edit, and submit this policy for approval. However, you **cannot approve** it because you are the uploader. This maker-checker process ensures that policies are reviewed by someone other than the creator.`}
            collapsible={true}
            defaultExpanded={false}
          />
        )}
        {policy?.fileStatus === "pending approval" && (
          <InformationStepsCard
            heading="**What should I do with this pending policy?**"
            subtitle={`This policy **${policy?.policyDisplayName
              }** has been submitted for approval by **${policyOwner?.name || "the policy uploader"
              }** hereby referred as the uploader. Only an admin who is **not the uploader**, can review and approve or reject this policy.`}
            steps={[
              "**Review the policy document** carefully to ensure all content is accurate, complete, and aligns with organizational standards.",
              "**Approve the policy** if it meets all requirements. Only admins who are **not the uploader** can approve this policy, ensuring proper separation of duties in the maker-checker process.",
              "**Reject the policy** if it needs revisions. Provide clear feedback so the uploader can make necessary changes and resubmit.",
              "Once approved, the policy becomes **active** and will be available for **all employees** to view and acknowledge.",
            ]}
            footerText={`💡 As an **approver**, you play a critical role in the maker-checker process. Only admins who are **not the uploader** can approve this policy. Once approved, it will be available for all employees to acknowledge, helping maintain compliance and security standards across the organization.`}
            collapsible={true}
            defaultExpanded={false}
          />
        )}
        {policy?.fileStatus === "active" && (
          <InformationStepsCard
            heading="**What should I do with this active policy?**"
            subtitle={`This policy **${policy?.policyDisplayName}** is now **active** and has been approved. It is available for **all employees** to view and acknowledge.`}
            steps={[
              "**Review the policy document** to see the final approved version that employees will acknowledge.",
              "**Acknowledge the policy** You can acknowledge the policy by visiting the [employee policies page](/employee/policies) to accept and acknowledge this policy.",
              "**Monitor employee acknowledgments** by visiting the [People page](/people) to see which employees have accepted this policy and track compliance status.",
              "**Track acknowledgment progress** to ensure all assigned employees complete their required policy acknowledgments within the specified timeframe.",
              "**Edit or update the policy** if needed by creating a new version. The current active version will remain available until the new version is approved and activated.",
              "Once all employees acknowledge the policy, it helps maintain compliance and ensures everyone is aligned with the organization's security and compliance standards.",
            ]}
            footerText={`💡 This policy is now **active** and available for employee acknowledgment. Visit the [People page](/people) to review who has accepted this policy and track overall compliance status across your organization.`}
            collapsible={true}
            defaultExpanded={false}
          />
        )}

        {/* Analyzing policy — when draft/pending but file not yet valid */}
        {(policy?.fileStatus === "draft" || policy?.fileStatus === "pending approval") &&
          policy?.is_file_valid == null && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Analyzing policy. Approval options will appear when analysis is complete.
              </Typography>
            </Box>
          )}
        {/* Approval suggestion card — shown when draft or pending approval, file valid, and backend found details */}
        {(policy?.fileStatus === "draft" || policy?.fileStatus === "pending approval" || policy?.fileStatus === "reject") &&
          policy?.is_file_valid != null && (
            <PolicyApprovalSuggestionCard
              loading={approvalDetailsLoading}
              approvalDates={approvalDetails?.approvalDates ?? []}
              possibleApprovers={approvalDetails?.possibleApprovers ?? []}
              staffList={staffList}
              onConfirm={async (approvalDate, approverEmployeeId) => {
                await handleUpdateStatus(
                  policy?.fileUuid,
                  "active",
                  null,
                  approvalDate || undefined,
                  approverEmployeeId || undefined
                );
                refetch();
              }}
            />
          )}

        <Grid
          container
          spacing={3}
          sx={{ alignItems: "stretch", mt: 3 }}
        >
          {/* Left Column – no scroll here; scroll is inside ChecksStatusCard */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Box
              sx={{
                width: "100%",
                maxHeight: "min(800px, calc(100vh - 220px))",
                minHeight: "520px",
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                overflow: "hidden",
              }}
            >
              <DetailCard
                policy={policy}
                approvedBy={policyApprover}
                policyOwner={policyOwner}
              />
              <ChecksStatusCard policy={policy} refetchTrigger={refetchTrigger} />
            </Box>
          </Grid>

          {/* Right Column – same max height, viewer scrolls inside */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Box
              sx={{
                width: "100%",
                maxHeight: "min(800px, calc(100vh - 220px))",
                minHeight: "400px",
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                backgroundColor: "#F3F4F6",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.06)",
                padding: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <PdfViewer
                  fileUrl={`${process.env.REACT_APP_CLOUDFRONT_URL}/${isDocxPolicy
                    ? policy?.filePath?.replace(/\.docx$/i, ".pdf")
                    : policy?.filePath
                    }`}
                  fillHeight
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <GroupMembersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          members={activeStaffs}
        />
        <UploadPolicyDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          policyData={policy}
          close={() => setOpenEditDialog(false)}
          refetch={refetch}
          version={Number(policy?.fileVersion) + 1}
          isCreate={false}
        />

        <DownLoadTemplateDialog
          open={openTemplateDialog}
          onClose={() => setOpenTemplateDialog(false)}
          policyData={policy}
        />
        <ConfirmDialog
          open={openRemoveDialog}
          onClose={() => setOpenRemoveDialog(false)}
          onConfirm={async () => {
            try {
              const id = Number(policy?.policyId);
              if (!id) {
                throw new Error("Missing policy id for deletion.");
              }
              const { data: deleteData } = await deleteTenantDocument({
                variables: { id },
              });
              const result = deleteData?.deleteTenantDocument;
              if (!result?.success) {
                throw new Error(result?.message || "Failed to delete policy.");
              }
              setSnackbarInfo({
                message: "Policy deleted successfully.",
                vertical: "top",
                horizontal: "right",
              });
              setOpenRemoveDialog(false);
              navigate("/admin/policies");
            } catch (err) {
              setOpenRemoveDialog(false);
              setSnackbarInfo({
                message: err?.message || "Failed to delete policy.",
                vertical: "top",
                horizontal: "right",
              });
            }
          }}
          heading="Remove policy?"
          content="This will remove all the versions of the policy and all the checks mapped to this policy, clear any employee acknowledgments associated with it, and may impact your overall compliance score."
          buttonText="Remove"
          loading={deletingPolicy}
        />

        <RejectPolicyDialog
          open={open}
          onClose={() => setOpen(false)}
          onReject={handleReject}
          policyName={`${policy?.policyDisplayName}: v${policy?.fileVersion}`}
          isRejecting={isRejecting}
        />

      </Box>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default PolicyDetails;
